/**
 * WalkingTourContent.js
 *
 * Content section for walking tour cards.
 * Displays tour-specific information like stops, duration, distance, difficulty.
 */

import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../shared/Icon';
import { formatDistance as formatDistanceUtil } from '../../../utils/distanceFormat';
import useDistanceUnit from '../../../hooks/useDistanceUnit';

/**
 * Format distance for display
 */
const formatCardDistance = (distance, unit) => {
  if (!distance) return null;

  // If distance is in meters
  if (typeof distance === 'number') {
    return formatDistanceUtil(distance, unit);
  }

  // If distance is already formatted string
  return distance;
};

/**
 * Difficulty badge
 */
const DifficultyBadge = ({ difficulty }) => {
  const { t } = useTranslation('feedCard');
  if (!difficulty) return null;

  const config = {
    easy: { labelKey: 'walkingTourContent.difficulty.easy', bgClass: 'bg-success-light', textClass: 'text-success' },
    moderate: { labelKey: 'walkingTourContent.difficulty.moderate', bgClass: 'bg-warning-light', textClass: 'text-warning-dark' },
    challenging: { labelKey: 'walkingTourContent.difficulty.challenging', bgClass: 'bg-warning-light', textClass: 'text-warning-dark' },
    difficult: { labelKey: 'walkingTourContent.difficulty.difficult', bgClass: 'bg-error-light', textClass: 'text-error' }
  };

  const { labelKey, bgClass, textClass } = config[difficulty] || config.moderate;

  return (
    <span className={`px-2 py-0.5 ${bgClass} ${textClass} text-xs rounded-full`}>
      {t(labelKey)}
    </span>
  );
};

/**
 * Main WalkingTourContent Component
 *
 * Props:
 * @param {Object} data - Normalized card data
 */
const WalkingTourContent = ({ data }) => {
  const { t } = useTranslation('feedCard');
  const distanceUnit = useDistanceUnit();

  /**
   * Format duration for display
   */
  const formatDuration = (duration) => {
    if (!duration) return null;

    // If duration is in minutes
    if (typeof duration === 'number') {
      if (duration >= 60) {
        const hours = Math.floor(duration / 60);
        const mins = duration % 60;
        return mins > 0
          ? t('walkingTourContent.duration.hoursMinutes', { hours, mins })
          : t('walkingTourContent.duration.hoursOnly', { hours });
      }
      return t('walkingTourContent.duration.minutesOnly', { minutes: duration });
    }

    // If duration is already formatted string
    return duration;
  };

  const { title, description, location, typeData } = data;
  const {
    stopCount = 0,
    duration,
    distance,
    difficulty,
    startPoint
  } = typeData || {};

  return (
    <div className="flex-grow">
      {/* Title */}
      <h3 className="font-semibold text-content-primary mb-2 line-clamp-1">
        <Icon name="walking" className="w-4 h-4 inline mr-1" />{title}
      </h3>

      {/* Tour stats row */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-content-secondary mb-2">
        {stopCount > 0 && (
          <span className="flex items-center">
            <Icon name="mapPin" className="w-3.5 h-3.5 mr-1" />
            {t('walkingTourContent.stopCount', { count: stopCount })}
          </span>
        )}
        {duration && (
          <span className="flex items-center">
            <Icon name="stopwatch" className="w-3.5 h-3.5 mr-1" />
            {formatDuration(duration)}
          </span>
        )}
        {distance && (
          <span className="flex items-center">
            <Icon name="ruler" className="w-3.5 h-3.5 mr-1" />
            {formatCardDistance(distance, distanceUnit)}
          </span>
        )}
      </div>

      {/* Difficulty and start point */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <DifficultyBadge difficulty={difficulty} />
        {startPoint && (
          <span className="text-xs text-content-muted">
            {t('walkingTourContent.startsAt', { startPoint })}
          </span>
        )}
      </div>

      {/* Location */}
      {location && (
        <p className="text-sm text-content-secondary mb-2 line-clamp-1">
          <Icon name="mapPin" className="w-3.5 h-3.5 inline mr-1" />{location}
        </p>
      )}

      {/* Description */}
      {description && (
        <p className="text-sm text-content-secondary mb-3 line-clamp-2">
          {description}
        </p>
      )}
    </div>
  );
};

export default memo(WalkingTourContent);
